import { Icon } from '@yes.technology/react-toolkit'
import { useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'
import {
  ExpandedButton,
  StyledChartWrapper,
  StyledHierarchyCard
} from '../Cockpit.styles'
import {
  CardChartCustomization,
  ChartCustomization,
  ChartProps,
  ChartTemplate,
  RefreshDataOptionsProps
} from '../Cockpit.types'
import useExpandedChartStyles from '../hooks/useExpandedChartStyles/useExpandedChartStyles'
import BarChart from './BarChart'
import BrazilMap from './BrazilMap'
import CardChart from './CardChart'
import DataTablesChart from './DataTablesChart'
import EmbeddedMasterdataChart from './EmbeddedMasterdataChart'
import GoogleChart from './GoogleChart'
import PieChart from './PieChart'
import PollingCardChart from './PollingCardChart'
import ResultGoalCardChart from './ResultGoalCardChart/ResultGoalCardChart'
import SaFChart, { SaFChartProps } from './SaFChart'

export type DataChartValue = string | number | null

export type DataChart = [
  string,
  DataChartValue,
  string?,
  string?,
  string?,
  DataChartValue?,
  DataChartValue?,
  string?
][]

const customCharts = [
  'BarChart',
  'GeoChart',
  'CardChart',
  'PieChart',
  'PollingCardChart',
  'SaFChart',
  'DataTables',
  'ResultGoalCardChart',
  'EmbeddedMasterdataChart'
]

const ChartWrapper = ({
  uuid,
  des,
  dataset,
  template,
  column = 'col-md-12'
}: ChartProps) => {
  const { t } = useTranslation()

  const [expandedChart, setExpandedChart] = useState(false)

  const chartData: DataChart | SaFChartProps['data'] = useMemo(() => {
    try {
      if (!dataset) return []
      return typeof dataset === 'string' ? JSON.parse(dataset) : []
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return []
    }
  }, [dataset])

  const chartTemplate: ChartTemplate = useMemo(() => {
    try {
      return typeof template === 'string' ? JSON.parse(template) : {}
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return {}
    }
  }, [template])

  const chartRef = useRef<HTMLDivElement>(null)
  const chartWrapperRef = useRef<HTMLDivElement>(null)

  const expandedChartStyles = useExpandedChartStyles({
    chartRef,
    chartWrapperRef,
    expandedChart,
    shouldFillAllAvailableSpace: ['EmbeddedMasterdataChart'].includes(
      chartTemplate?.chart_model
    )
  })

  return (
    <StyledChartWrapper
      $expanded={expandedChart}
      className={`mb-3 ${column}`}
      ref={chartWrapperRef}
    >
      <StyledHierarchyCard style={{ position: 'relative', height: '100%' }}>
        {!chartTemplate?.hide_expansion_button && (
          <ExpandedButton
            title={`${expandedChart ? t('cockpit.chart.collapse-button-label') : t('cockpit.chart.expand-button-label')}`}
            onClick={() => setExpandedChart(!expandedChart)}
          >
            <Icon
              iconName={`${expandedChart ? 'Contract' : 'Expand'}`}
              icSize='semiLarge'
            />
          </ExpandedButton>
        )}

        <div ref={chartRef} style={expandedChartStyles}>
          {chartTemplate.chart_model === 'BarChart' && (
            <BarChart
              chartModel={chartTemplate.chart_model}
              data={chartData as DataChart}
              options={chartTemplate.options}
              title={des}
            />
          )}

          {chartTemplate.chart_model === 'CardChart' && (
            <CardChart
              chartModel={chartTemplate.chart_model}
              data={[...(chartData as DataChart)].slice(1)}
              title={des}
              iconSize={chartTemplate?.options?.icon?.size}
            />
          )}

          {chartTemplate.chart_model === 'ResultGoalCardChart' && (
            <ResultGoalCardChart
              data={chartData as DataChart}
              title={des}
              customization={
                chartTemplate.chart_customization as CardChartCustomization
              }
              chartTemplate={chartTemplate}
            />
          )}

          {chartTemplate.chart_model === 'PieChart' && (
            <PieChart
              data={chartData as DataChart}
              title={des}
              options={chartTemplate.options}
              chartLanguage={chartTemplate?.chart_language}
            />
          )}

          {chartTemplate.chart_model === 'PollingCardChart' && (
            <PollingCardChart
              refreshDataOptions={
                chartTemplate.refresh_data_options as RefreshDataOptionsProps
              }
              chartModel={chartTemplate.chart_model}
              title={des}
              measurementUnit={chartTemplate?.measurement_unit}
            />
          )}

          {chartTemplate.chart_model === 'GeoChart' && (
            <BrazilMap
              chartModel={chartTemplate.chart_model}
              data={chartData as DataChart}
              options={chartTemplate.options}
              title={des}
              format={chartTemplate?.format}
              chartCustomization={
                chartTemplate?.chart_customization as ChartCustomization
              }
              displayNationalMinimap={chartTemplate?.display_national_mini_map}
              expandedScale={expandedChartStyles?.transform}
              mapLegend={chartTemplate?.map_legend}
              customOffsets={chartTemplate?.custom_offsets}
            />
          )}

          {chartTemplate.chart_model === 'SaFChart' && (
            <SaFChart data={chartData as SaFChartProps['data']} title={des} />
          )}

          {chartTemplate.chart_model === 'DataTables' && (
            <DataTablesChart props={chartTemplate?.props} title={des} />
          )}

          {chartTemplate.chart_model === 'EmbeddedMasterdataChart' && (
            <EmbeddedMasterdataChart props={chartTemplate?.props} title={des} />
          )}

          {/* Render GoogleChart for all other chart models */}

          {!customCharts.includes(chartTemplate.chart_model) && (
            <GoogleChart
              chartUuid={uuid}
              chartModel={chartTemplate.chart_model}
              data={chartData as DataChart}
              options={chartTemplate.options}
              title={des}
              chartLanguage={chartTemplate?.chart_language}
            />
          )}
        </div>
      </StyledHierarchyCard>
    </StyledChartWrapper>
  )
}

export default ChartWrapper
